/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CalendarWorkdays, GroupedTicket } from '@touchplan/ticket';
import * as strings from '@touchplan/strings';
import { DayChip } from '../day-chip-list/day-chip.model';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, takeUntil } from 'rxjs/operators';
import { WorkDayUtils } from '../../../../utils/work-day-utils';
import { DateUtils } from '../../../../utils/date-utils';
import { DefaultLoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../../logging/LoggerServiceFactory';

@Component({
	selector: 'tp-work-day-toggle',
	templateUrl: './work-day-toggle.component.html',
	styleUrls: ['./work-day-toggle.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: WorkDayToggleComponent,
		},
		LoggerServiceFactory.provide('WorkDayToggleComponent'),
	],
})
export class WorkDayToggleComponent implements ControlValueAccessor, OnDestroy, OnInit {
	@Input({ required: true }) public liveStart$!: Observable<string | undefined>;
	@Input({ required: true }) public calendarWorkdays!: CalendarWorkdays;
	@Input({ required: true }) public editTicket$!: Observable<GroupedTicket>;

	public ticketWorkdaysOn$ = new BehaviorSubject<boolean[]>([]);
	public workdayChips$ = new BehaviorSubject<DayChip[]>([]);

	public appStrings = strings;

	private shutdownSubject$ = new Subject<void>();

	// for ControlValueAccessor
	public isDisabled = false;
	private onValueChange = (_: boolean[]) => {};
	private onTouched = () => {};

	constructor(private logger: DefaultLoggerService) {}

	public ngOnInit(): void {
		combineLatest([this.ticketWorkdaysOn$, this.editTicket$, this.liveStart$])
			.pipe(
				map(([ticketWorkdaysOn, ticket, liveStart]) => {
					const roleId = ticket.view.liveRole?.$id || ticket.view.roleId;
					return WorkDayUtils.calculateWorkDays(this.calendarWorkdays, ticketWorkdaysOn, liveStart, roleId);
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((workdays: Array<{ date: Date; enabled: boolean; startOfWork: boolean }>) => {
				const chips = workdays.map(({ date, enabled, startOfWork }) => {
					const weekDay = DateUtils.getWeekDayMonthDay(date);
					return {
						id: date.toString(),
						label: `${weekDay.weekDay} ${weekDay.dayOfMonth}`,
						isOn: enabled,
						styleClass: startOfWork ? 'start-of-work' : '',
						disabled: startOfWork,
					};
				});
				this.workdayChips$.next(chips);
			});

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
		// when ticketWorkdaysOn$ changes externally to this component, possibly need to trigger value changes
		// so work schedule is informed or handle that some other way
		// this.onValueChange(workdaysOn);
		// END PLACEHOLDER REPLACE AS PART OF TP-7368
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public changeWorkDay(chip: DayChip) {
		const currentChips = this.workdayChips$.getValue();
		const updatedChips = currentChips.map((c) => (c.id === chip.id ? { ...c, isOn: !c.isOn } : { ...c }));
		const workdaysOn = updatedChips.map((c) => c.isOn);
		this.workdayChips$.next(updatedChips);
		this.ticketWorkdaysOn$.next(workdaysOn);

		this.onTouched();
		this.onValueChange(workdaysOn);
	}

	public writeValue(obj: any): void {
		if (obj === null || obj === undefined) {
			obj = [];
		}
		if (!Array.isArray(obj)) {
			this.logger.warn("<workdays> component's FormControl value is not an array", obj);
			return;
		}
		if (obj.some((item) => typeof item !== 'boolean')) {
			this.logger.warn("<workdays> component's FormControl value is not an array of booleans", obj);
			return;
		}
		// cast obj after validation
		this.ticketWorkdaysOn$.next(obj as boolean[]);
	}

	/**
	 * ControlValueAccessor function:
	 * This method is called by Angular to give this class a callback method, which is to be called
	 * when the (externally visible) value of this component should be updated.
	 */
	public registerOnChange(fn: (_: boolean[]) => void): void {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	/**
	 * ControlValueAccessor function: to disable the component
	 * @param isDisabled true to disable the component
	 */
	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}
}
